/**
 * Backend API Client
 *
 * Handles all communication with the Python backend API
 * Replaces direct OpenWeatherMap API calls
 */

import axios from 'axios';

// Backend URL - update this with your Render deployment URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Search for weather by city name or zip code
 * @param {string} query - City name or zip code
 * @param {string} units - Units of measurement (imperial, metric, standard)
 * @returns {Promise} Weather data
 */
export const searchWeather = async (query, units = 'imperial') => {
  try {
    const response = await axios.post(`${BACKEND_URL}/api/weather/search`, {
      query,
      units
    });
    return response.data;
  } catch (error) {
    console.error('Error searching weather:', error);
    throw new Error(
      error.response?.data?.error ||
      'Failed to search weather. Please try again.'
    );
  }
};

/**
 * Find cities with similar weather conditions
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {number} temp - Current temperature
 * @param {string} condition - Current weather condition
 * @param {string} units - Units of measurement
 * @returns {Promise} Array of cities with similar weather
 */
export const findSimilarWeather = async (lat, lon, temp, condition, units = 'imperial') => {
  try {
    const response = await axios.post(`${BACKEND_URL}/api/weather/similar`, {
      lat,
      lon,
      temp,
      condition,
      units
    });
    return response.data;
  } catch (error) {
    console.error('Error finding similar weather:', error);
    throw new Error(
      error.response?.data?.error ||
      'Failed to find similar weather locations. Please try again.'
    );
  }
};

/**
 * Find cities with opposite weather conditions
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {number} temp - Current temperature
 * @param {string} condition - Current weather condition
 * @param {string} units - Units of measurement
 * @returns {Promise} Array of cities with opposite weather
 */
export const findOppositeWeather = async (lat, lon, temp, condition, units = 'imperial') => {
  try {
    const response = await axios.post(`${BACKEND_URL}/api/weather/opposite`, {
      lat,
      lon,
      temp,
      condition,
      units
    });
    return response.data;
  } catch (error) {
    console.error('Error finding opposite weather:', error);
    throw new Error(
      error.response?.data?.error ||
      'Failed to find opposite weather locations. Please try again.'
    );
  }
};

/**
 * Health check - verify backend is running
 * @returns {Promise} Health status
 */
export const checkBackendHealth = async () => {
  try {
    const response = await axios.get(`${BACKEND_URL}/health`);
    return response.data;
  } catch (error) {
    console.error('Backend health check failed:', error);
    return { status: 'unhealthy', error: error.message };
  }
};
